// The guided tour: builds its own steps from the derived customer path and the per-frame byte
// budget, then walks them, highlighting the diagram as it goes. Extracted out of the topology
// Stimulus controller (which grew past 1000 lines) into a plain class the controller instantiates
// with its own DOM elements and data — the controller's startTour/endTour/tourStep stay as thin
// delegations, since Stimulus's data-action wiring (and the portaled-overlay rebinding in
// overlays.ts, which dispatches by method NAME) needs those exact names to keep resolving on the
// controller instance itself.
import DOMPurify from 'dompurify';
// Magenta for the guided tour's current "action" node, matching the original reference page's
// .tour-action colour.
const TOUR_ACTION_COLOR = '#ec4899';
// SVG filter ids for the tour glows. Referenced from components/_topology.scss as `filter: url(#…)`
// — keep both sides in sync. See ensureGlowFilters() for why these exist instead of CSS drop-shadow.
const GLOW_DEFS_ID = 'topology-glow-defs';
const GLOW_PATH_FILTER_ID = 'topology-glow-path';
const GLOW_ACTION_FILTER_ID = 'topology-glow-action';
// Mermaid 11.16.1 renders node ids as "mermaid-<renderId>-flowchart-<ID>-<index>" (verified against
// the actual rendered DOM — a bare "^flowchart-" prefix never matched here since the real string
// starts with "mermaid-<timestamp>-", which silently made every tour-step match fail and disabled
// highlighting/auto-scroll entirely). Strip everything up to and including the last "-flowchart-"
// plus the trailing "-<index>" so tour-step prefix matching compares against the ID exactly as
// assigned in the .erb template. Exported: handleMermaidClick (inspector.ts) needs the identical
// extraction to match inspector_data keys against the real DOM id.
export function extractNodeId(domId) {
    return domId.replace(/^.*-flowchart-/, '').replace(/-\d+$/, '');
}
// Mermaid renders edge ids as "mermaid-<renderId>-L_<SRC>_<DST>_<index>", but both SRC and DST can
// themselves contain underscores (e.g. "HUB_RTR_BRSAORT06"), so a plain "_" split can't reliably
// tell where the source ends and the target begins. Instead, try each already-known node id (longest
// first, to prefer the most specific match) as a candidate source and check whether the remainder is
// *exactly* another known node id — unambiguous since we're matching against real, enumerated ids
// rather than guessing a split point.
export function extractEdgeEndpoints(edgeDomId, knownIds) {
    const stripped = edgeDomId.replace(/^.*-L_/, '').replace(/_\d+$/, '');
    const candidates = [...knownIds].sort((a, b) => b.length - a.length);
    for (const src of candidates) {
        if (!stripped.startsWith(src + '_'))
            continue;
        const rest = stripped.slice(src.length + 1);
        if (knownIds.includes(rest))
            return [src, rest];
    }
    return null;
}
export class Tour {
    constructor(host, els, getTrafficPath, getFrameBudget) {
        this.host = host;
        this.els = els;
        this.getTrafficPath = getTrafficPath;
        this.getFrameBudget = getFrameBudget;
        this.currentStep = 0;
        this.steps = [];
    }
    get currentTourStep() { return this.currentStep; }
    get tourSteps() { return this.steps; }
    // WebKit/Safari computes CSS `filter: drop-shadow(...)` on SVG child elements but never paints it —
    // verified by pixel-diffing the rendered halo (Chromium painted it, WebKit painted nothing), which
    // is why the tour's glow was invisible in Safari while the computed styles looked correct. The
    // portable route is a real SVG <filter> referenced as `filter: url(#id)`, which is also what the
    // original reference page fell back to. Injected once per document, as a hidden defs-only <svg>;
    // `url(#…)` resolves document-wide, so a single copy serves every diagram/tab.
    //
    // Improvement over the original, which could only offer a *static* Safari glow: the pulse is driven
    // by a SMIL <animate> on the primitive's stdDeviation rather than a CSS keyframe, so the animation
    // survives in Safari too and every browser gets the identical effect from one code path.
    ensureGlowFilters() {
        if (document.getElementById(GLOW_DEFS_ID))
            return;
        const holder = document.createElement('div');
        holder.innerHTML = `
      <svg id="${GLOW_DEFS_ID}" aria-hidden="true" focusable="false" width="0" height="0"
           style="position:absolute;width:0;height:0;overflow:hidden;pointer-events:none">
        <defs>
          <filter id="${GLOW_PATH_FILTER_ID}" x="-60%" y="-60%" width="220%" height="220%">
            <feDropShadow dx="0" dy="0" stdDeviation="3" flood-color="#38bdf8" flood-opacity="0.9"/>
          </filter>
          <filter id="${GLOW_ACTION_FILTER_ID}" x="-80%" y="-80%" width="260%" height="260%">
            <feDropShadow dx="0" dy="0" stdDeviation="3" flood-color="${TOUR_ACTION_COLOR}" flood-opacity="1">
              <animate attributeName="stdDeviation" values="2.5;7;2.5" dur="1.6s" repeatCount="indefinite"/>
            </feDropShadow>
          </filter>
        </defs>
      </svg>`;
        const svg = holder.firstElementChild;
        if (svg)
            document.body.appendChild(svg);
    }
    // data-topology-tour-param picks which tour; absent means the path tour, so an older trigger that
    // does not pass one keeps working.
    start(kind = 'path') {
        if (!this.els.popup)
            return;
        this.build(kind);
        this.currentStep = 0;
        this.els.popup.style.display = 'block';
        this.render();
    }
    end() {
        if (this.els.popup) {
            this.els.popup.style.display = 'none';
        }
        this.clearHighlights();
    }
    step(delta) {
        const isLastStep = this.currentStep >= this.steps.length - 1;
        // "Next" on the last step means "Finish" (see render(), which relabels the button) — close the
        // tour instead of silently no-op'ing at the clamped boundary.
        if (delta > 0 && isLastStep) {
            this.end();
            return;
        }
        this.currentStep += delta;
        if (this.currentStep < 0)
            this.currentStep = 0;
        if (this.currentStep >= this.steps.length)
            this.currentStep = this.steps.length - 1;
        this.render();
    }
    destroy() {
        this.clearHighlights();
    }
    // The tour traces ONE packet down the ACTUAL customer path — TopologyPresenter::TrafficPaths'
    // derived hops, never the standby/transit tunnels or hubs, which stay dimmed for the whole tour.
    // Slicing `hops` (rather than composing an id from a bare NNI string, `TUNNEL_${nni}`) is what
    // fixed the tour silently pointing at the wrong node on a circuit with two paths sharing one NNI
    // (BTBR.BA73.IP002 dials the same NNI twice, once over PPTP and once over L2TP) — the diagram
    // itself appends a protocol suffix in that case, and there was previously no way for this
    // controller to know to. hops are also what makes a bypassing circuit (the EoIP routed straight
    // out, never touching the L2TP paths at all) trace its REAL path instead of narrating the overlay
    // it does not use, and what makes a path that stops mid-way — no election, or an endpoint
    // mismatch the audit caught — end the tour exactly where the configuration stops answering,
    // saying why, instead of narrating a hop that was never actually drawn.
    // Two tours over the same diagram, because they answer different questions and merging them made
    // every step too long to read. The PATH tour follows the geography — which hub, which VLAN, which
    // tunnel is forwarding. The ENCAPSULATION tour ignores geography and follows one frame's byte
    // budget. Both are built from the same derived values, so they cannot contradict each other.
    build(kind = 'path') {
        const tp = this.getTrafficPath() || {};
        const hops = tp.hops || [];
        const bypassing = tp.rides_overlay === false;
        const tunnelInfo = tp.tunnel || {};
        const hubLabel = tunnelInfo.hub?.identity || 'the Provider Hub';
        // Paths that tie on the deciding bridge's path-cost never reach a tunnel at all, so they carry
        // no hops past the bridge — the tied candidates are reported separately (see TrafficPaths#path)
        // as the bare ids the diagram uses for each of them, and the tour composes VLAN_<n>/TUNNEL_<n>
        // the same way the (single, elected) case would have.
        const tied = tp.undetermined || [];
        const tiedCost = tp.undetermined_cost ?? '';
        const idx = (id) => hops.indexOf(id);
        // The hop range [fromId, toId), or [fromId, end of hops) when toId is absent or never reached —
        // which is exactly what a stopped path looks like, and is the case this exists to trace rather
        // than hide.
        const between = (fromId, toId) => {
            const i = idx(fromId);
            if (i < 0)
                return [];
            const j = toId ? idx(toId) : -1;
            return (toId && j > i) ? hops.slice(i, j) : hops.slice(i);
        };
        const keep = (ids) => ids.filter(Boolean);
        // Segments mirror the tour's five narrative beats. The named boundaries (CPE_WAN, HUB_RTR_<hub>,
        // PRIMARY_HUB_NNI/PRIMARY_HUB_EOIP) are hops #derive_customer_path always emits at the same
        // relative point whenever the path reaches that far, so slicing between them tracks whatever
        // shape THIS circuit's path actually has instead of assuming the overlay one.
        const eoipIdx = idx('EOIP_CPE');
        const segCustomerOrigin = between('C_AES', 'EOIP_CPE');
        const segCpeEgress = eoipIdx >= 0 ? hops.slice(eoipIdx, eoipIdx + 2) : [];
        const afterEoipIdx = eoipIdx >= 0 ? eoipIdx + 2 : -1;
        const hubRtrId = hops.find(h => h.startsWith('HUB_RTR_')) || '';
        const hubRtrIdx = hubRtrId ? idx(hubRtrId) : -1;
        const segProviderCore = afterEoipIdx >= 0
            ? (hubRtrIdx > afterEoipIdx ? hops.slice(afterEoipIdx, hubRtrIdx) : hops.slice(afterEoipIdx))
            : [];
        const hubEndId = hops.includes('PRIMARY_HUB_NNI') ? 'PRIMARY_HUB_NNI'
            : hops.includes('PRIMARY_HUB_EOIP') ? 'PRIMARY_HUB_EOIP'
                : '';
        let segHubLeg = hubRtrId ? between(hubRtrId, hubEndId || undefined) : [];
        let segDelivery = hubEndId ? between(hubEndId) : [];
        // An endpoint mismatch (apply_endpoint_break!) can stop the path EXACTLY on PRIMARY_HUB_NNI —
        // the hub's own service VLAN was reached, but decapsulation (the next hop) never happened.
        // Narrating "the hub passes the packet on to the EoIP endpoint" over that single boundary hop
        // would describe something that has not happened, so it is folded into the hub-termination step
        // instead of standing alone as a "decapsulation and delivery" step with nothing delivered. Does
        // not apply to a bypassing path's PRIMARY_HUB_EOIP boundary: there, arriving IS decapsulating —
        // there is no separate landing hop before it the way PRIMARY_HUB_NNI is one for an elected path.
        if (hubEndId === 'PRIMARY_HUB_NNI' && segDelivery.length <= 1) {
            segHubLeg = segHubLeg.concat(segDelivery);
            segDelivery = [];
        }
        // ---- encapsulation narration -------------------------------------------------------------
        // Every number below is read from frameBudgetValue, which FrameBudget derived from the device
        // configuration. Nothing here computes a byte count of its own: a second implementation is how
        // the tour and the audit would come to disagree, and a byte count an engineer acts on is the
        // worst thing to be casually wrong about.
        const fb = this.getFrameBudget() || {};
        const cust = fb.customer || {};
        const lim = fb.limits || {};
        const mss = fb.mss || {};
        const jumbo = fb.jumbo || {};
        const layers = fb.layers || [];
        const B = (n) => (n === undefined || n === null ? '?' : `${n} B`);
        // A running table of the stack, so the growth is visible rather than asserted.
        const layerTable = layers.length
            ? `<table class="tour-bytes"><thead><tr><th>Layer</th><th>Adds</th><th>Running</th></tr></thead><tbody>` +
                layers.map(l => `<tr><td>${l.name}<div class="text-muted small">${l.detail}</div></td>` +
                    `<td>+${l.adds}</td><td>${l.running}</td></tr>`).join('') +
                `</tbody></table>`
            : '';
        const frag = fb.fragmentation || {};
        // A bridge that states no mtu is not a flat 1500: RouterOS runs it at the lowest MTU of its ports
        // (mtu=auto), which on CLBR.4411.E001 is an 8156 customer port. Saying "the default of 1500 applies"
        // there quoted the wrong number and sent the reader to the bridge instead of the port that sets it.
        const mtuNote = cust.l3_mtu_is_default
            ? (cust.l3_mtu_limited_by
                ? `<p class="text-muted small">The customer bridge states no <code>mtu</code>, so RouterOS runs it at
             the lowest MTU of its ports — ${B(cust.l3_mtu)}, set by <code>${cust.l3_mtu_limited_by}</code>.
             Changing that port changes this ceiling; stating the bridge's <code>mtu</code> makes it a decision.${cust.l3_mtu_bounded_by
                    ? ` It is at most that: <code>${cust.l3_mtu_bounded_by}</code> sits on a bridge PPP sessions attach to, and VLANs like it have been measured running at 1450.`
                    : ''}</p>`
                : `<p class="text-muted small">The customer bridge states no <code>mtu</code>, and no port gives it a
             size the configuration shows, so it runs at ${B(cust.l3_mtu)}. Unset and stated behave identically
             on the wire, but only one of them was a decision.</p>`)
            : '';
        // The CPE/hub-side encapsulation node ids the encap tour's own steps need, read from the same
        // hops the path tour slices rather than rebuilt from a bare NNI string.
        const vlan = hops.find(h => h.startsWith('VLAN_')) || '';
        const tunnel = hops.find(h => h.startsWith('TUNNEL_')) || '';
        const hubRtr = hubRtrId;
        const hubPptp = hops.find(h => h.startsWith('HUB_PPTP_')) || '';
        const hubVpn = hops.find(h => h.startsWith('HUB_VPN_')) || '';
        const hubVlan = hops.find(h => h.startsWith('HUB_VLAN_')) || '';
        const nniLabel = tunnel ? tunnel.replace('TUNNEL_', '') : 'the active tunnel';
        // ---- path tour ------------------------------------------------------------------------------
        const pathTour = [];
        let tiedStepShown = false;
        // "This is a Layer 2 hand-off" was asserted to every circuit, and FrameBudget's own MSS section
        // disagrees on four of them: two whose customer port is genuinely not a member of the bridge that
        // feeds the tunnel (customer_port_not_on_uni_bridge already reports it on the Audit tab, and
        // BTBR.SP19.IP035 is the sharpest — its UNI bridge holds the EoIP and nothing else), and two
        // whose port could not be identified at all. The tour reads the same derivation rather than
        // repeating a sentence that is true of most circuits.
        const routedHere = mss.customer_traffic_is_routed_here;
        const portKnown = mss.customer_port_identified !== false;
        if (segCustomerOrigin.length) {
            pathTour.push({
                title: 'Customer origin',
                body: `<p>A raw Ethernet frame arrives from the customer network on
               <strong>${cust.interface || 'the CPE LAN interface'}</strong>.</p>` +
                    (routedHere === true
                        ? `<p><strong>&#9888; It does not reach the tunnel from here.</strong> The port
                   commented as the customer's is not a member of
                   <strong>${cust.bridge || 'the UNI bridge'}</strong>, the bridge that feeds this
                   circuit's EoIP, so this hand-off is not the Layer 2 one the diagram draws. The
                   Audit tab has the detail.</p>`
                        : !portKnown
                            ? `<p>Which physical port is the customer's could not be read from this
                     configuration — more than one candidate sits on
                     <strong>${cust.bridge || 'the UNI bridge'}</strong> — so whether the frame is
                     bridged straight into the tunnel here is not something the config settles.</p>`
                            : `<p>It is bridged into <strong>${cust.bridge || 'the UNI bridge'}</strong>.
                     Nothing is routed here: this is a Layer 2 hand-off, so the CPE forwards the frame
                     without looking at the IP inside it.</p>`),
                nodes: segCustomerOrigin,
                action: segCustomerOrigin[segCustomerOrigin.length - 1] || ''
            });
        }
        if (segCpeEgress.length) {
            pathTour.push(bypassing ? {
                title: 'Routed, not tunnelled',
                body: `<p>This circuit's EoIP does not ride the redundant L2TP paths below — its packets are
               routed directly out of this CPE's own IP stack instead of being bridged onto the
               overlay's NNI bridge.</p>
               <p class="tour-aside">The paths further down this diagram may be up and authenticated,
               but this customer's frames never reach them.</p>`,
                nodes: segCpeEgress,
                action: segCpeEgress[1] || segCpeEgress[0] || ''
            } : {
                title: 'EoIP encapsulation',
                body: `<p>The raw frame enters the EoIP tunnel, gaining GRE and IP headers, and the resulting
               packet leaves through the <strong>main NNI bridge</strong>.</p>
               <p class="tour-aside">What this costs in bytes is the subject of the Encapsulation tour.</p>`,
                nodes: segCpeEgress,
                action: segCpeEgress[1] || segCpeEgress[0] || ''
            });
        }
        if (tied.length > 1) {
            tiedStepShown = true;
            pathTour.push({
                title: 'Provider transport — which path is undetermined',
                body: `<p>The packet leaves over <strong>one</strong> of ${tied.join(', ')} — but the configuration does not say which.</p>` +
                    `<p>All of them tie on the deciding bridge at path-cost <strong>${tiedCost}</strong>. With equal costs, spanning tree falls through to the designated <em>port</em> id, which is the port priority plus the internal interface number. A RouterOS export does not contain that number, and it can change when an interface is recreated or the device reboots &mdash; so this is not a value that can be read from the config at all.</p>` +
                    `<p>Ask the device instead:</p><pre>/interface bridge port monitor [find bridge=&lt;deciding bridge&gt;]</pre>` +
                    `<p>The port reporting <code>role: root-port</code> / <code>forwarding</code> is the live answer.</p>`,
                nodes: keep([...tied.map((n) => `VLAN_${n}`), ...tied.map((n) => `TUNNEL_${n}`), 'R_CPE', 'CPE_WAN', 'ISP_GW', 'VPN_CLOUD']),
                action: 'VPN_CLOUD'
            });
        }
        else if (segProviderCore.length) {
            pathTour.push({
                title: 'Provider transport',
                // The second sentence is about paths this circuit may not have: CLBR.1911.E013 declares one
                // tunnel, and telling its reader that "the other paths stay authenticated and up" describes
                // redundancy the circuit is missing — which its own insufficient_redundant_paths finding is
                // on the Audit tab to say. declared_paths travels with the path for exactly this.
                body: `<p>The NNI bridge forwards the packet down the active <strong>${nniLabel}</strong>
               tunnel, which carries it across the public internet to the provider.</p>` +
                    ((tp.declared_paths ?? 0) > 1
                        ? `<p>The other paths stay authenticated and up — spanning tree is blocking them, which
                   is not the same as the tunnels being down.</p>`
                        : `<p class="tour-aside">This circuit declares no second path, so there is nothing for
                   spanning tree to block and nothing to fail over to.</p>`),
                nodes: segProviderCore,
                action: segProviderCore.find(h => h.startsWith('TUNNEL_')) || segProviderCore[segProviderCore.length - 1] || ''
            });
        }
        if (segHubLeg.length) {
            pathTour.push(bypassing ? {
                title: 'Routed to the tunnel endpoint',
                body: `<p>The packet reaches <strong>${hubLabel}</strong>, which routes it directly to the
               interface holding this EoIP's own endpoint address rather than handing it to a PPP
               session — there is no service VLAN on this leg.</p>`,
                nodes: segHubLeg,
                action: segHubLeg[segHubLeg.length - 1] || ''
            } : {
                title: 'Hub termination',
                body: `<p>The packet reaches <strong>${hubLabel}</strong>, where the PPP session terminates and
               the payload lands on the hub's VPN bridge, then the service VLAN.</p>`,
                nodes: segHubLeg,
                action: segHubLeg.find(h => h.startsWith('HUB_PPTP_')) || segHubLeg[segHubLeg.length - 1] || ''
            });
        }
        if (segDelivery.length) {
            pathTour.push({
                title: 'Decapsulation and delivery',
                body: bypassing
                    ? `<p>The EoIP endpoint decapsulates the packet directly — there is no service VLAN hand-off
             on this leg — and delivers the original customer frame to the provider core.</p>`
                    : `<p>The hub passes the packet via the service VLAN to the core <strong>EoIP
             endpoint</strong>, which strips the encapsulation and delivers the original customer
             frame to the provider core.</p>`,
                nodes: segDelivery,
                action: segDelivery.find(h => h === 'PRIMARY_HUB_EOIP') || segDelivery[segDelivery.length - 1] || ''
            });
        }
        // A path that stops before the end says where and why (TrafficPaths §1) — the tour says the
        // same thing, once, appended to whichever step it actually reached, rather than narrating a hop
        // that was never drawn. Not appended when the tied-election step already explained itself in
        // full: that step's own body IS the explanation, and repeating it would read as two findings.
        if (!tiedStepShown && tp.status && tp.status !== 'flowing' && tp.reason && pathTour.length) {
            pathTour[pathTour.length - 1].body +=
                `<p class="tour-aside"><strong>The tour stops here:</strong> ${tp.reason}</p>`;
        }
        // A path tour with no steps opened a blank popup: every segment above is conditional on `hops`,
        // and with no derived path there is nothing to slice, so the panel appeared with an empty title
        // and body and a "1 / 0" counter. Says what happened instead, and quotes the derivation's own
        // reason where it recorded one rather than inventing an explanation. The encapsulation tour got
        // the same treatment when it became dynamic; this is the half that was left behind.
        if (!pathTour.length) {
            pathTour.push({
                title: 'No path could be traced',
                body: `<p>This circuit's configuration does not yield a path for a customer frame to follow,
               so there are no hops to walk.</p>` +
                    (tp.reason
                        ? `<p><strong>The derivation stopped because:</strong> ${tp.reason}</p>`
                        : `<p>Nothing was recorded about where it stops either, which usually means the
                   topology was generated before the path derivation ran — regenerating it is the
                   first thing to try.</p>`) +
                    `<p class="tour-aside">The Config Observations and Audit tabs list what could and could
               not be read from the devices.</p>`,
                nodes: [],
                action: ''
            });
        }
        // Numbered after assembly, not written into each title: the steps above are conditional, and a
        // path that emits some of them used to show "Step 1, Step 3, Step 4" against a counter reading
        // "1 / 3". No circuit in the estate skips one today — every one of the 63 derived paths emits a
        // contiguous prefix — so this is a guard on the numbering, not a repair of a visible fault.
        pathTour.forEach((s, i) => { s.title = `Step ${i + 1}: ${s.title}`; });
        // ---- encapsulation tour ---------------------------------------------------------------------
        // Assembled from the budget rather than fixed at five steps. The static version narrated the
        // same five to every circuit, which meant it described hops a circuit does not have (a
        // transparent hand-off was still told about MRRU and multilink reassembly) and skipped the ones
        // it does: the hub-side MTUs — the client bridge, the hub's own end of the EoIP, the core bridge
        // — are where a half-done jumbo change actually goes wrong, and none of them was ever a step.
        //
        // Every step below is emitted only when the fact behind it exists, so the step COUNT is this
        // circuit's own encapsulation depth: one per layer FrameBudget priced, one per hop that states
        // an MTU, and nothing for a layer the frame never crosses.
        const encapTour = [];
        // FrameBudget names each layer's ROLE rather than the tour matching on display names — two of
        // these node ids (the S-VLAN sub-interface and the tunnel) are composed per circuit from the
        // hops above and cannot be named from a service class at all.
        const nodeForRole = { eoip: 'EOIP_CPE', vlan: vlan, ppp: tunnel };
        const ceilings = jumbo.ceilings || [];
        const mtuAt = (node) => ceilings.find(c => c.node === node)?.mtu;
        const encapLayers = layers.filter(l => l.role !== 'ethernet');
        if (cust.interface) {
            const portOverBridge = cust.port_mtu && cust.l3_mtu && Number(cust.port_mtu) > cust.l3_mtu;
            encapTour.push({
                // Opens on the PORT, not the bridge. The port is the first hop a customer frame actually
                // meets, and it can state an MTU of its own — on 23 circuits it states one ABOVE the bridge
                // behind it, which is the half-done jumbo change customer_port_mtu_above_uni_bridge reports.
                // Starting at the bridge skipped the hop where that intent is visible.
                title: 'What the customer may send',
                body: `<p>The first hop every customer frame meets is
               <strong>${cust.interface}</strong>${cust.port_mtu ? `, which states <strong>mtu=${cust.port_mtu}</strong>`
                    : ', which states no <code>mtu</code> of its own'}.</p>` +
                    (portOverBridge
                        ? `<p><strong>&#9888; The ceiling is set one hop later.</strong> This port is raised to
                   ${cust.port_mtu} but ${cust.bridge || 'the bridge behind it'} carries only
                   ${B(cust.l3_mtu)} — a frame the bridge will not carry never reaches the tunnel,
                   whatever the port allows.</p>`
                        : '') +
                    `<p class="tour-aside">The 4-byte FCS is excluded throughout this tour: it is regenerated
               hop by hop and never travels inside a tunnel.</p>`,
                nodes: keep(['C_AES', 'CPE_LAN']),
                action: 'CPE_LAN'
            });
        }
        if (cust.bridge) {
            encapTour.push({
                title: 'The ceiling',
                body: `<p>The port bridges into <strong>${cust.bridge}</strong>. This is the ceiling every
               later number is measured from.</p>
               <p><strong>Largest frame accepted:</strong> ${B(cust.max_frame)}
               — ${B(cust.l3_mtu)} of payload plus a 14 B Ethernet header.</p>
               <p>Nothing is routed here: this is a Layer 2 hand-off, so the CPE forwards the frame
               without looking at the IP inside it.</p>${mtuNote}`,
                nodes: ['CPE_UNI'],
                action: 'CPE_UNI'
            });
        }
        // One step per layer the frame actually gains, in the order FrameBudget stacked them. A circuit
        // with no tunnel gets none of these; a double-tagged one gets its tag step priced at 8 B,
        // because the depth was read off the device's own vlan chain.
        // What actually travels. The running total is one full-size frame with every header, which on a PPP
        // path never exists on the wire: multilink cuts it into session-sized pieces first, each framed by L2TP
        // on its own. Quoting that total as "leaves this CPE as" gave a jumbo circuit a 9000-byte packet on a
        // 1500-byte internet link.
        const wireNote = (l) => {
            if (l.role !== 'ppp') {
                return `<p><strong>On the wire:</strong> a ${B(cust.l3_mtu)} customer payload leaves this CPE as
                ${B(l.running)}.</p>`;
            }
            if (!frag.session_mtu || !frag.frame_on_ppp) {
                return `<p><strong>Before multilink:</strong> one full-size frame with every header is
                ${B(l.running)}. What reaches the wire depends on the session it rides, which the
                configuration here does not give.</p>`;
            }
            if (frag.pieces === 1) {
                return `<p><strong>On the wire:</strong> the largest frame reaching the session is
                ${B(frag.frame_on_ppp)}, inside the session's ${B(frag.session_mtu)}, so it crosses as one
                packet of about ${B(frag.frame_on_ppp + 40)}.</p>`;
            }
            if (!frag.pieces) {
                return `<p><strong>&#9888; On the wire:</strong> the largest frame reaching the session is
                ${B(frag.frame_on_ppp)}, above the session's ${B(frag.session_mtu)}, and no multilink is
                negotiated to cut it — frames above the session size do not cross this path.</p>`;
            }
            return `<p><strong>On the wire:</strong> the largest frame reaching the session is
              ${B(frag.frame_on_ppp)}. The session runs at ${B(frag.session_mtu)}, so multilink PPP cuts it
              into <strong>${frag.pieces} pieces</strong> of at most ${B(frag.session_mtu)} — about
              ${B(frag.piece_on_wire)} each once L2TP frames them. The ${B(l.running)} above is the frame
              before it is cut, never one packet on the wire.</p>`;
        };
        // The NNI bridge carries the EoIP's outer packet, and it is the one place the DF setting matters: the
        // packet fits, is fragmented, or — under dont-fragment=inherit — is dropped. The same arithmetic as the
        // Audit tab's nni_bridge_too_small_for_eoip and eoip_df_inherit_drops, read from FrameBudget.
        const nniStep = () => {
            if (!frag.nni_bridge || !frag.nni_result || frag.nni_result === 'not_on_nni_bridge')
                return null;
            const over = (frag.outer_packet || 0) - (frag.nni_mtu || 0);
            const passes = (frag.nni_mtu || 0) - 42;
            // An EoIP that states no mtu runs at the MTU of the interface it leaves by less 42 (measured on BRSAORT06 and
            // CLBR.6411's CPE, 2026-09-15), so the budget quotes that size rather than assuming the customer bridge's.
            const assumed = frag.tunnel_mtu_source === 'auto'
                ? ` The tunnel states no <code>mtu</code>, so RouterOS runs it at ${B(frag.tunnel_mtu)}: the MTU of
           <code>${frag.tunnel_mtu_egress}</code>, the interface it leaves by, less 42.`
                : frag.tunnel_mtu
                    ? ''
                    : ` The tunnel states no <code>mtu</code>, so this assumes it takes the frames the customer bridge hands it.`;
            const sizing = frag.nni_limited_by
                ? ` — it states no <code>mtu</code>, so RouterOS runs it at the lowest MTU of its ports (<code>${frag.nni_limited_by}</code>)`
                : '';
            const result = frag.nni_result === 'fits'
                ? `<p><strong>It fits</strong>, with ${B(-over)} to spare, so the packet crosses whole.${(frag.outer_packet_tagged || 0) > (frag.nni_mtu || 0)
                    ? ` A customer frame carrying its own two VLAN tags is ${B(frag.outer_packet_tagged)}, which does not.`
                    : ''}</p>`
                : frag.nni_result === 'fragmented'
                    ? `<p><strong>&#9888; It does not fit</strong> — ${B(over)} over. The CPE fragments the outer
               packet in IP, and the hub reassembles it: it arrives intact, but every customer packet above
               ${B(passes)} is split in two and rebuilt in software.</p>`
                    : `<p><strong>&#9888; It does not fit</strong> — ${B(over)} over — and this tunnel sets
               <code>dont-fragment=inherit</code>. A customer packet above ${B(passes)} that sets DF is
               <strong>dropped here</strong>, and the ICMP "too big" goes to the router's own tunnel address,
               never to a Layer 2 customer, so it looks like a black hole. Multilink further on ignores DF,
               but the drop happens here, before it.</p>`;
            // A VLAN port on a PPP-attached bridge can run below its mtu (1450 measured on BRSAORT06), and an unset
            // bridge follows its ports' actual-mtu, so the size above is only an upper bound there.
            const bounded = frag.nni_bounded_by
                ? `<p class="tour-aside">${B(frag.nni_mtu)} is an upper bound: <code>${frag.nni_bounded_by}</code> sits on a
           bridge PPP sessions attach to, and VLANs like it have been measured running at 1450, which this bridge would
           then run at too.</p>`
                : '';
            return {
                title: 'The NNI bridge',
                body: `<p>The EoIP's outer packet leaves by <strong>${frag.nni_bridge}</strong>, which carries
               <strong>${B(frag.nni_mtu)}</strong>${sizing}.</p>
               <p>The largest customer payload the tunnel is handed is ${B(frag.largest_payload)}, which the
               EoIP wraps as <strong>${B(frag.outer_packet)}</strong> (plus 42 B).${assumed}</p>` + result + bounded,
                nodes: ['CPE_NNI'],
                action: 'CPE_NNI'
            };
        };
        // Whether the last-mile step follows the layers: the PPP layer's own step says what comes next.
        const lastMileStep = Boolean(frag.last_mile && frag.session_mtu);
        encapLayers.forEach((l, i) => {
            const node = nodeForRole[l.role || ''] || '';
            const isLast = i === encapLayers.length - 1;
            const lead = l.role === 'eoip'
                ? `<p>The whole customer frame, headers and all, becomes the <em>payload</em> of a new IP
             packet. This is where a Layer 2 service starts costing Layer 3 bytes.</p>`
                : l.role === 'vlan'
                    ? `<p>The packet leaves the NNI bridge on a tagged sub-interface, and each 802.1Q tag is
               charged to the same budget.</p>`
                    : `<p>The packet is handed to the ${l.name} session that carries it across the public
               internet, which frames it again.</p>`;
            const tail = l.role === 'eoip'
                ? (lim.eoip_mtu
                    ? `<p>The EoIP interface itself is set <code>mtu=${lim.eoip_mtu}</code>.</p>`
                    : `<p>The EoIP interface states no <code>mtu</code>, so the largest frame it is handed is the
                 customer bridge's.</p>`) +
                    // Whether the outer packet is split or dropped is not decided here: the EoIP wraps the frame
                    // whole, and only the interface it leaves by can be too small for it. The old text said
                    // "dropped" to every inherit tunnel, including the 1500-byte circuits whose packet fits.
                    ((frag.dont_fragment || lim.eoip_dont_fragment) === 'inherit'
                        ? `<p><strong>Outer packet:</strong> <code>dont-fragment=inherit</code>, so a customer packet
                 that sets DF passes its DF bit to the outer packet. That matters only if the outer packet
                 is too big for the interface it leaves by — the next step.</p>`
                        : `<p><strong>Outer packet:</strong> <code>dont-fragment=${frag.dont_fragment || lim.eoip_dont_fragment || 'no'}</code>${frag.dont_fragment_is_default || !lim.eoip_dont_fragment ? ' (not stated; RouterOS\'s default)' : ''}, so the outer packet never carries DF and the router may split it — if it is too big for the
                 interface it leaves by, the next step.</p>`)
                : l.role === 'ppp'
                    ? (lastMileStep
                        ? `<p>Below the IP layer, multilink PPP does its own fragmenting: the pieces leave by the
                   WAN in the next step, and the hub rebuilds them in the one after.</p>`
                        : `<p>Below the IP layer, multilink PPP does its own fragmenting — which is the next
                   step.</p>`)
                    : '';
            encapTour.push({
                title: l.name,
                body: lead +
                    `<p><strong>${l.name}</strong> adds <strong>${l.adds} B</strong> — ${l.detail} — taking
               the frame to <strong>${B(l.running)}</strong>.</p>` +
                    tail +
                    (isLast ? layerTable + wireNote(l) : ''),
                nodes: keep([node, l.role === 'eoip' ? 'CPE_NNI' : '', l.role === 'ppp' ? 'R_CPE' : '',
                    l.role === 'ppp' ? 'CPE_WAN' : '']),
                action: node || 'CPE_NNI'
            });
            if (l.role === 'eoip') {
                const step = nniStep();
                if (step)
                    encapTour.push(step);
            }
        });
        // The WAN each piece leaves by. A session larger than its last mile fragments every full-size piece in
        // IP (ppp_session_exceeds_last_mile, 15 circuits on PPPoE), which no earlier step could show.
        //
        // Before the first step on the hub, in the order the frame travels: the pieces leave the CPE by its WAN,
        // cross the last mile, and only then reach the hub that rebuilds them. After Reassembly, the tour lit the
        // hub and then came back to the CPE's WAN for this step.
        if (lastMileStep && frag.last_mile) {
            const lm = frag.last_mile;
            const over = (frag.piece_on_wire || 0) - lm.carries;
            encapTour.push({
                title: 'The last mile',
                body: `<p>Each piece leaves by <strong>${lm.interface}</strong>${lm.type ? ` (${lm.type})` : ''}, which
               carries <strong>${B(lm.carries)}</strong>${lm.stated === false ? ' — no <code>mtu</code> stated, so RouterOS\'s default' : ''}.
               A piece is at most ${B(frag.session_mtu)}, about ${B(frag.piece_on_wire)} once L2TP frames it.</p>` +
                    (lm.fits
                        ? `<p><strong>It fits</strong>, so pieces cross the internet whole.</p>`
                        : `<p><strong>&#9888; Each full-size piece is ${B(over)} too big for it</strong>, so the CPE
                   fragments every one in IP on top of the multilink cut. The Audit tab reports it; lowering the
                   session's <code>max-mtu</code> to fit is the fix.</p>`),
                nodes: keep(['R_CPE', 'CPE_WAN']),
                action: 'CPE_WAN'
            });
        }
        // Reassembly is a property of a PPP layer. A circuit that has none is not told about an MRRU it
        // does not negotiate — the old fixed step said "there is nothing to reassemble", which is a step
        // spent saying a step does not apply.
        if (encapLayers.some(l => l.role === 'ppp')) {
            const mrruShort = frag.mrru_effective_shortfall || (lim.mrru_sufficient === false ? lim.mrru_shortfall : undefined);
            encapTour.push({
                title: 'Reassembly',
                body: `<p><strong>Multilink PPP</strong> is what lets a frame larger than the underlying link
               cross it at all: PPP splits it (4 B per fragment header, long sequence numbers) and the
               far end rebuilds it up to the negotiated <code>MRRU</code>. It only works when
               <em>both</em> ends negotiate it.</p>
               <table class="tour-bytes"><tbody>
                 <tr><td>Largest frame the NNI bridge can emit</td><td>${B(lim.mrru_required)}</td></tr>
                 <tr><td>MRRU this CPE's client asks for</td><td>${lim.mrru ? B(lim.mrru) : '<strong>not set</strong>'}</td></tr>
                 <tr><td>MRRU the hub's PPP server sets (server-wide)</td><td>${lim.hub_mrru ? B(lim.hub_mrru) : '<strong>not set</strong>'}</td></tr>
                 ${frag.mrru_effective ? `<tr><td><strong>MRRU the session uses</strong> (the lower end)</td><td>${B(frag.mrru_effective)}</td></tr>` : ''}
               </tbody></table>` +
                    // Judged on the MRRU the session actually uses. The CPE's own figure said "sufficient" on
                    // paths whose hub end is lower, and the hub's value was attributed to a PPP profile, which has
                    // no such parameter.
                    (mrruShort
                        ? `<p><strong>&#9888; Short by ${mrruShort} B.</strong> The bridge is ${B(lim.nni_mtu)}, and
                   a frame leaving it carries a 14 B Ethernet header and its 802.1Q tag, so it needs
                   ${B(lim.mrru_required)}. Frames above ${B(frag.mrru_effective || lim.mrru)} are discarded
                   inside the tunnel with no ICMP at all.${frag.frame_on_ppp && frag.frame_on_ppp <= (frag.mrru_effective || lim.mrru || 0)
                            ? ` No customer frame reaches that today: the largest arriving here is ${B(frag.frame_on_ppp)}.`
                            : ''}</p>`
                        : '') +
                    (lim.reassembly_possible === false
                        ? (lim.mrru
                            ? `<p><strong>&#9888; The hub's PPP server sets no <code>mrru</code></strong>, so multilink
                       is not negotiated and nothing is reassembled — the CPE's MRRU is moot until the hub
                       matches it.</p>`
                            : `<p><strong>&#9888; This CPE's client sets no <code>mrru</code></strong>, so multilink is
                       not negotiated and nothing larger than the session crosses.</p>`)
                        : ''),
                nodes: keep([hubRtr, hubPptp, hubVpn, hubVlan]),
                action: hubPptp || hubRtr
            });
        }
        // The far end's own ceilings, one step each. These are the hops a jumbo change forgets: the
        // tunnel is raised at both ends and the bridges behind them are left at 1500, which reads as
        // done and carries nothing larger than before.
        ceilings.filter(c => (c.node || '').startsWith('PRIMARY_HUB_')).forEach(c => {
            const cpeEoip = mtuAt('EOIP_CPE');
            const mismatch = c.node === 'PRIMARY_HUB_EOIP' && cpeEoip !== undefined && cpeEoip !== c.mtu;
            encapTour.push({
                title: `The hub end: ${c.hop}`,
                body: (c.source === 'bounded'
                    ? `<p><code>${c.hop}</code> states no <code>mtu</code>, so RouterOS runs it at the lowest actual MTU
                   of its ports: <strong>at most ${B(c.mtu)}</strong>. <code>${c.bounded_by}</code> sits on a bridge PPP
                   sessions attach to, and VLANs like it have been measured running at 1450 on BRSAORT06, which would
                   lower it.</p>`
                    : c.source === 'ports'
                        ? `<p><code>${c.hop}</code> states no <code>mtu</code>, so RouterOS runs it at the lowest MTU
                   of its ports: <strong>${B(c.mtu)}</strong>.</p>`
                        : `<p><code>${c.hop}</code> states <strong>${B(c.mtu)}</strong>.</p>`) +
                    (c.node === 'PRIMARY_HUB_NNI' && c.payload !== undefined
                        ? `<p>It carries the hub EoIP's outer packet, so a customer MTU of at most ${B(c.payload)} crosses
                   it whole (42 B of EoIP and 8 B of customer tags).</p>`
                        : '') +
                    (mismatch
                        ? `<p><strong>&#9888; The two ends of this EoIP are not the same size.</strong> The CPE
                   end is ${B(cpeEoip)} and this end is ${B(c.mtu)}. EoIP negotiates nothing — each end
                   caps its own direction — so the smaller of the two is the real ceiling in that
                   direction only, and the circuit is asymmetric.</p>`
                        : '') +
                    ((jumbo.limiting_payload_node ?? jumbo.limiting_node) === c.node
                        ? `<p><strong>This is the narrowest hop on the path</strong>, so it caps the whole
                   circuit whatever the CPE side allows.</p>`
                        : ''),
                nodes: [c.node],
                action: c.node
            });
        });
        // The summary: every ceiling side by side, with the tour pointing at whichever one actually caps
        // the circuit rather than at a fixed node.
        // Compared as the customer MTU each hop carries whole, the one unit a bridge carrying the EoIP and a
        // bridge carrying the customer's frame share. Raw MTUs side by side told CLBR.1111.E013 its path was
        // "consistently sized" with an 8994 NNI bridge behind an 8996 customer bridge.
        if (ceilings.length) {
            const limHop = jumbo.limiting_payload_hop || jumbo.limiting_hop;
            const limSize = jumbo.limiting_payload ?? jumbo.limiting_mtu;
            const shortfalls = jumbo.shortfall_hops || [];
            const belowTunnel = (jumbo.below_tunnel_hops || []).filter(b => !shortfalls.some(s => s.hop === b.hop));
            const hasPayload = ceilings.some(c => c.payload !== undefined);
            const list = (hops) => hops.map(h => `<code>${h.hop}</code> (${B(h.payload)})`).join(', ');
            encapTour.push({
                title: 'Jumbo frames end to end',
                body: (shortfalls.length
                    ? `<p><strong>&#9888; Full-size customer frames do not cross this path whole.</strong> The
                   customer may send ${B(jumbo.customer_payload)}, and ${list(shortfalls)} ${shortfalls.length > 1 ? 'carry' : 'carries'} less; what
                   happens to the excess at each hop is in its own step.</p>`
                    : jumbo.jumbo_unreachable
                        ? `<p><strong>&#9888; The jumbo capacity on this path cannot be reached.</strong> The
                     narrowest hop is <code>${limHop}</code> at ${B(limSize)}, so a larger MTU configured
                     further along can never be used by customer traffic.</p>`
                        : `<p>The narrowest hop is <code>${limHop || 'not determined'}</code> at ${B(limSize)}, and
                     every hop carries the customer's largest frame whole.</p>`) +
                    (belowTunnel.length
                        ? `<p><strong>&#9888; Sized below the tunnel.</strong> The EoIP is set to ${B(jumbo.tunnel_mtu)},
                   and ${list(belowTunnel)} cannot carry a frame that size. No customer frame reaches it while
                   the customer side stays smaller, but the Audit tab reports it: raising the customer side
                   alone would break the path.</p>`
                        : '') +
                    `<table class="tour-bytes"><thead><tr><th>Hop</th><th>MTU</th>${hasPayload ? '<th>Customer MTU carried</th>' : ''}</tr></thead><tbody>` +
                    ceilings.map(c => `<tr><td><code>${c.hop}</code></td><td>${B(c.mtu)}</td>${hasPayload ? `<td>${B(c.payload ?? c.mtu)}</td>` : ''}</tr>`).join('') +
                    `</tbody></table>` +
                    (hasPayload
                        ? `<p class="tour-aside">A bridge carrying the EoIP needs 50 B above the customer MTU it passes:
                   42 B of EoIP and 8 B for the customer's two VLAN tags. The MRRU also carries the 14 B
                   Ethernet header and the path's S-VID tag.</p>`
                        : ''),
                nodes: keep([...ceilings.map(c => c.node || ''), 'PRIMARY_HUB_SFP1']),
                action: (shortfalls[0]?.node) || jumbo.limiting_payload_node || jumbo.limiting_node || 'PRIMARY_HUB_EOIP'
            });
        }
        // A tour with no steps would render undefined and blank the popup. Nothing here is derivable
        // when the CPE config named no bridge, no tunnel and no MTU — which is a fact worth stating
        // rather than an empty overlay.
        if (!encapTour.length) {
            encapTour.push({
                title: 'No byte budget could be derived',
                body: `<p>This circuit's configuration names no customer bridge, tunnel or MTU, so there is
               no frame to follow. The Config Observations tab lists what could not be read.</p>`,
                nodes: [],
                action: ''
            });
        }
        // Numbered last, because the count is only known once the steps exist.
        encapTour.forEach((s, i) => { s.title = `Bytes ${i + 1}: ${s.title}`; });
        this.steps = kind === 'encap' ? encapTour : pathTour;
    }
    render() {
        if (!this.els.title || !this.els.body || !this.els.counter)
            return;
        const step = this.steps[this.currentStep];
        // A tour whose steps are derived can legitimately come out empty (a circuit with nothing to
        // trace), and reading .title off undefined would throw inside a click handler and leave the
        // popup open over a half-rendered step.
        if (!step)
            return;
        this.els.title.textContent = step.title;
        this.els.body.innerHTML = DOMPurify.sanitize(step.body);
        this.els.counter.textContent = `${this.currentStep + 1} / ${this.steps.length}`;
        const isFirstStep = this.currentStep === 0;
        const isLastStep = this.currentStep === this.steps.length - 1;
        if (this.els.prev) {
            this.els.prev.style.visibility = isFirstStep ? 'hidden' : 'visible';
        }
        if (this.els.next) {
            this.els.next.textContent = isLastStep ? 'Finish' : 'Next Step →';
        }
        this.highlightNodes();
    }
    // Cumulative: every step from 1 up to the current one contributes its nodes, so the diagram builds
    // up the active path traveled so far rather than replacing it each step (matching the original
    // reference page's tour). Everything off that path — standby tunnels, transit hubs — stays dimmed
    // throughout. The current step's single `action` node gets the magenta "you are here" pulse, again
    // as in the original.
    //
    // Improvement over the original: it only lit edges touching the *current* step, so earlier hops sat
    // there as highlighted-but-disconnected boxes. Here every edge whose BOTH endpoints are already on
    // the cumulative path lights up, drawing the traversed route as one continuous line, and edges
    // touching the current action node additionally animate so the newest hop still reads as in-motion.
    highlightNodes() {
        this.clearHighlights();
        const activeTab = this.host.getTabContentTargets().find(el => el.classList.contains('active'));
        if (!activeTab)
            return;
        const wrapper = activeTab.querySelector('.diagram-wrapper');
        if (!wrapper)
            return;
        wrapper.classList.add('tour-active');
        const step = this.steps[this.currentStep];
        if (!step)
            return;
        const actionId = step.action;
        const cumulative = new Set(this.steps.slice(0, this.currentStep + 1).flatMap(s => s.nodes));
        const allNodes = Array.from(wrapper.querySelectorAll('.node'));
        const highlightedIds = [];
        let actionNode = null;
        let firstStepNode = null;
        allNodes.forEach(node => {
            const id = extractNodeId(node.id || '');
            if (!cumulative.has(id))
                return;
            node.classList.add('highlighted-node');
            highlightedIds.push(id);
            if (id === actionId) {
                node.classList.add('tour-action');
                this.applyActionNodeStyles(node);
                actionNode = node;
            }
            if (!firstStepNode && step.nodes.includes(id))
                firstStepNode = node;
        });
        const allEdges = Array.from(wrapper.querySelectorAll('.edgePaths path'));
        allEdges.forEach(edge => {
            // The diagram carries a couple of `~~~` links that exist only to force dagre to rank the
            // primary hub below the standby hubs so it lands in the centre. They are not real network
            // paths, and .tour-edge paints an amber stroke with !important, so highlighting one would
            // draw a connection that does not exist on the devices.
            if (edge.classList.contains('edge-thickness-invisible'))
                return;
            const endpoints = extractEdgeEndpoints(edge.id || '', highlightedIds);
            if (!endpoints)
                return;
            const [src, dst] = endpoints;
            edge.classList.add('tour-edge');
            if (src === actionId || dst === actionId)
                edge.classList.add('tour-edge-current');
        });
        const scrollTarget = (actionNode || firstStepNode);
        if (scrollTarget) {
            scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
    }
    // Mermaid's own classDef rules (`#mermaid-<runtimeId> .someClass > *`) carry !important at
    // ID-level specificity, so no rule we can author in a stylesheet can recolour the action node's
    // outline — see the comment on .tour-action in components/_topology.scss. Inline styles set with
    // important priority are the one thing that outranks them, so the magenta stroke is applied here
    // and stripped again in clearHighlights. The marker attribute keeps that cleanup surgical so we
    // never clobber inline styles mermaid itself may have set.
    applyActionNodeStyles(node) {
        node.querySelectorAll(':scope > rect, :scope > polygon, :scope > circle, :scope > ellipse, :scope > path')
            .forEach(shape => {
            shape.setAttribute('data-tour-action-styled', '');
            shape.style.setProperty('stroke', TOUR_ACTION_COLOR, 'important');
            shape.style.setProperty('stroke-width', '3px', 'important');
        });
    }
    clearHighlights() {
        this.host.getTabContentTargets().forEach(tab => {
            const wrapper = tab.querySelector('.diagram-wrapper');
            if (wrapper) {
                wrapper.classList.remove('tour-active');
                wrapper.querySelectorAll('[data-tour-action-styled]').forEach(shape => {
                    shape.style.removeProperty('stroke');
                    shape.style.removeProperty('stroke-width');
                    shape.removeAttribute('data-tour-action-styled');
                });
                wrapper.querySelectorAll('.highlighted-node').forEach(node => {
                    node.classList.remove('highlighted-node', 'tour-action');
                });
                wrapper.querySelectorAll('.tour-edge').forEach(edge => {
                    edge.classList.remove('tour-edge', 'tour-edge-current');
                });
            }
        });
    }
}
